import { type PropsWithChildren, type ReactNode, useMemo } from 'react';

import { Button, Tooltip } from '@kc-console/common-ui';
import { useTranslation } from '@kc-console/core';
import IllustStatusStart from '@kep/kcds-icons/illust/IllustStatusStart';

import ExceptionBoundary from './components/ExceptionBoundary';

import type { ExceptionBoundaryProps } from './components/ExceptionBoundary';

export interface PageStartBoundaryProps
  extends Omit<ExceptionBoundaryProps, 'title' | 'description' | 'actions'> {
  handleStart: () => void;
  isActivating: boolean;
  description?: string;
  isStartDisabled?: boolean;
  startDisabledTooltip?: ReactNode;
  title?: string;
}

/**
 * 서비스 시작 안내 컴포넌트
 * @description
 * - 서비스가 활성화되지 않은 경우 기본 서비스 시작 안내 화면을 표시합니다.
 * @example
 * <PageStartBoundary
 *   handleStart={handleActivate}
 *   isLoading={isLoading}
 *   isActivating={isActivating}
 *   isValid={isActivated}
 * >
 *   <ServiceContent />
 * </PageStartBoundary>
 */
export default function PageStartBoundary({
  children,
  title,
  description,
  handleStart,
  isActivating,
  isStartDisabled = false,
  startDisabledTooltip,
  ...props
}: PropsWithChildren<PageStartBoundaryProps>) {
  const { t } = useTranslation();
  const pageTitle = title || t('layout:exception_page.start_service.title');
  const pageDescription = useMemo(() => {
    return (
      description || (
        <>
          {t('layout:exception_page.not_ready_boundary.message')}
          <br />
          {t('layout:exception_page.not_ready_boundary.instruction')}
        </>
      )
    );
  }, [description, t]);
  const actions = useMemo(() => {
    const isDisabled = isActivating || isStartDisabled;
    const button = (
      <Button variant="primary" size="lg" disabled={isDisabled} onClick={() => handleStart()}>
        {t('layout:exception_page.start_service')}
      </Button>
    );

    if (isStartDisabled && startDisabledTooltip) {
      return (
        <Tooltip message={startDisabledTooltip}>
          <span>{button}</span>
        </Tooltip>
      );
    }

    return button;
  }, [isActivating, isStartDisabled, startDisabledTooltip, handleStart, t]);
  return (
    <ExceptionBoundary
      title={pageTitle}
      description={pageDescription}
      illustration={<IllustStatusStart />}
      actions={actions}
      {...props}
    >
      {children}
    </ExceptionBoundary>
  );
}
